import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { LucideIcon } from 'lucide-react';
import type { DistributionItem } from '@/lens';
import { Panel } from '@/components/Panel';

/**
 * 분포를 그리는 **네 가지 형태**.
 *
 * ★★★ **형태가 데이터의 종류를 말한다**(2026-10-03, 사용자 지시 — 「전부 똑같은
 * 형태와 컬러라 분별이 안 된다」).
 *
 * ```
 * RankBars   순위 · 라벨이 길다          → 가로 막대   (동사별 시도 · 막힘 지점)
 * RateBars   비율 비교 · 기준선이 있다    → 가로 막대 + 기준선  (성공률 · 플래그)
 * HistoBars  구간 · 순서가 있다          → **세로 막대**  (DC 구간 · 응답 시간)
 * StackShare 구성비 · 합이 100%          → **가로 스택 한 줄**  (판정 결과 · 이탈 사유)
 * ```
 *
 * ⚠️⚠️ **집계가 준 줄을 더 자르거나 합치지 않는다.** 0건 줄도 그대로 그린다 —
 * 자르는 판단은 집계가 이미 했고, 화면이 다시 자르면 「0건」인지 「안 세는지」
 * 구분할 수 없게 된다.
 *
 * ★ `data-panel`/`data-row`/`data-cell` 은 **테스트가 잡는 자리**다. 어떤 형태든
 *   그 속성은 그대로 둔다 — 그래야 디자인을 바꿔도 값 대조가 산다.
 */

/** 테스트가 읽는 숨은 표. ★ 어떤 그림을 그리든 값은 여기서 읽힌다. */
function DataRows({ items }: { items: DistributionItem[] }) {
  return (
    <div className="sr-only" aria-hidden="true">
      {items.map((it) => (
        <div key={it.label} data-row={it.label}>
          <span data-cell="label">{it.label}</span>
          <span data-cell="value">{it.valueText}</span>
          <span data-cell="pct">{it.pctText}</span>
        </div>
      ))}
    </div>
  );
}

const TONE: Record<string, string> = {
  good: 'var(--lens-success)',
  warn: 'var(--lens-warning)',
  bad: 'var(--lens-error)',
};

const tooltipStyle = {
  background: 'var(--lens-surface)',
  border: '1px solid var(--lens-border)',
  borderRadius: 'var(--lens-radius-sm)',
  fontSize: 14,
  padding: '6px 10px',
} as const;

/**
 * 패널 격자 — ★★ **짧은 패널 둘을 나란히** 둔다.
 *
 * ⚠️ 전에는 전부 한 줄씩 쌓여 **세로로 길고 가로로 넓었다**(사용자 지적 —
 * 「한눈에 들어오지 않는다」). ★ 짧은 것을 묶으면 **스크롤이 줄고 비교가 쉽다.**
 */
export function PanelGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-xl lg:grid-cols-2">{children}</div>;
}

export interface ChartPanelProps {
  title: string;
  note?: string | undefined;
  items: DistributionItem[];
  /** 축색. ★ 화면마다 다르다 — `var(--lens-axis-*)`. */
  color: string;
  emptyText?: string | undefined;
  /**
   * 제목 아래 **보이는 한 줄**.
   *
   * ⚠️⚠️ **값은 `note`(툴팁)에 넣지 않는다.** `note` 는 「왜 이렇게 세는가」
   * 전용이다. ★ 실측으로 두 번 틀렸다 — `lensBlockedSplit` 과
   * `lensLatencySummary` 를 툴팁에 숨겨 **화면에서 수치가 사라졌다.**
   */
  lead?: string | undefined;
  /** 제목 앞 아이콘(lucide). ⚠️ 이모지는 쓰지 않는다 — SUIT 에 없다. */
  icon?: LucideIcon | undefined;
  /** 등장 순서 — 격자에서 패널이 차례로 떠오른다. */
  delay?: number | undefined;
}

function Shell({
  title,
  note,
  items,
  children,
  emptyText,
  lead,
  icon,
  delay,
  color,
}: ChartPanelProps & { children: React.ReactNode }) {
  return (
    <Panel title={title} note={note} testId={title} icon={icon} delay={delay} accent={color}>
      {lead !== undefined && lead !== '' ? (
        <p className="mb-lg text-body-lg">{lead}</p>
      ) : null}
      {items.length === 0 ? (
        <p className="text-body-md text-muted-foreground">{emptyText ?? '표본이 없습니다.'}</p>
      ) : (
        <>
          <DataRows items={items} />
          {children}
        </>
      )}
    </Panel>
  );
}

/**
 * 순위 가로 막대 — 라벨이 길고 **순서가 뜻을 갖는** 것.
 *
 * ★ 막대 길이는 `pct` 다. 집계가 「점유율」로 쓸 때도 「성공률」로 쓸 때도 있다.
 */
export function RankBars(p: ChartPanelProps) {
  const max = Math.max(...p.items.map((i) => i.pct), 1);
  return (
    <Shell {...p}>
      <ul className="space-y-sm">
        {p.items.map((it, i) => (
          <li key={it.label} className="grid grid-cols-[10rem_1fr_auto] items-center gap-md">
            <span className="truncate text-title-sm" title={it.label}>
              {it.label}
            </span>
            <span className="h-7 overflow-hidden rounded-sm bg-[var(--lens-bg)]">
              <span
                className="animate-sweep flex h-full items-center justify-end rounded-sm pr-sm text-label-sm text-white"
                style={{
                  width: `${Math.max(2, (it.pct / max) * 100)}%`,
                  background: TONE[it.tone] ?? p.color,
                  animationDelay: `${i * 45}ms`,
                }}
              >
                {it.pct / max > 0.18 ? it.valueText : ''}
              </span>
            </span>
            <span className="w-16 text-right text-title-sm tabular-nums">{it.pctText}</span>
          </li>
        ))}
      </ul>
    </Shell>
  );
}

/**
 * 비율 막대 + **기준선** — 「높을수록/낮을수록 좋다」가 있는 것.
 *
 * ★ 기준선이 있으면 막대 하나하나를 읽지 않고 **넘었는지만** 봐도 된다.
 */
export function RateBars({ floorPct, ...p }: ChartPanelProps & { floorPct?: number }) {
  return (
    <Shell {...p}>
      <ul className="space-y-sm">
        {p.items.map((it, i) => {
          const low = floorPct !== undefined && it.pct <= floorPct;
          return (
            <li key={it.label} className="grid grid-cols-[10rem_1fr_auto] items-center gap-md">
              <span className="truncate text-title-sm" title={it.label}>
                {it.label}
              </span>
              <span className="relative h-7 overflow-hidden rounded-sm bg-[var(--lens-bg)]">
                <span
                  className="animate-sweep block h-full rounded-sm"
                  style={{
                    width: `${Math.max(2, Math.min(100, it.pct))}%`,
                    background: low ? 'var(--lens-error)' : p.color,
                    animationDelay: `${i * 45}ms`,
                  }}
                />
                {floorPct !== undefined ? (
                  <span
                    className="absolute inset-y-0 w-px bg-[var(--lens-text-muted)]"
                    style={{ left: `${Math.min(100, floorPct)}%` }}
                    title={`기준선 ${floorPct}%`}
                  />
                ) : null}
              </span>
              <span className="w-24 text-right text-title-sm tabular-nums">
                <span className="text-muted-foreground">{it.valueText}</span>
                <span className="ml-sm">{it.pctText}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </Shell>
  );
}

/**
 * 히스토그램(세로 막대) — **구간**이고 순서가 있는 것.
 *
 * ★★ 구간은 x축이 자연스럽다. 가로 막대로 그리면 「구간이 늘어선 모양」이
 * 안 보인다. ⚠️ 0건 구간도 자리를 지킨다 — 그것이 정보다.
 */
export function HistoBars(p: ChartPanelProps) {
  const data = p.items.map((it) => ({ name: it.label, pct: it.pct, raw: it }));
  return (
    <Shell {...p}>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 24, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid vertical={false} stroke="var(--lens-border)" />
            <XAxis
              dataKey="name"
              tickLine={false}
              axisLine={{ stroke: 'var(--lens-border)' }}
              tick={{ fontSize: 14, fill: 'var(--lens-text-muted)' }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={44}
              tick={{ fontSize: 13, fill: 'var(--lens-text-muted)' }}
              tickFormatter={(v: number) => `${v}%`}
            />
            <Tooltip
              cursor={{ fill: 'var(--lens-bg)' }}
              contentStyle={tooltipStyle}
              formatter={(_v, _n, item) => {
                const r = (item as { payload?: { raw?: DistributionItem } }).payload?.raw;
                return [`${r?.valueText ?? ''} · ${r?.pctText ?? ''}`, ''];
              }}
            />
{/* ⚠️⚠️ **recharts 애니메이션은 끈다.** 켰더니 디자인 시스템 카드가
              **애니메이션 도중에 찍혀** 도넛이 조각 하나로 나왔다(2026-10-04 실측).
              캡처 파이프라인이 정착을 기다려 주지 않고, `prefers-reduced-motion`
              을 강제할 수단도 없다.
              ★ 움직임은 CSS 쪽(`animate-rise`/`animate-sweep`)이 진다 — 그쪽은
              짧고 `backwards` 로 채워져 **찍히는 시점에 이미 끝나 있다.** */}
            <Bar dataKey="pct" radius={[6, 6, 0, 0]} isAnimationActive={false}>
              {data.map((d) => (
                <Cell key={d.name} fill={TONE[d.raw.tone] ?? p.color} />
              ))}
              <LabelList
                dataKey="raw"
                position="top"
                content={({ x, y, width, index }) => (
                  <text
                    x={Number(x) + Number(width) / 2}
                    y={Number(y) - 8}
                    textAnchor="middle"
                    fontSize={13}
                    fill="var(--lens-text-muted)"
                  >
                    {data[index as number]?.raw.valueText}
                  </text>
                )}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Shell>
  );
}

/**
 * 구성비 — **가로 스택 한 줄** + 범례.
 *
 * ★★ 합이 100% 인 것은 **한 줄로 보여야** 「무엇이 큰 조각인가」가 한눈에 온다.
 * ⚠️ 막대 여러 개로 그리면 **합이 100% 라는 사실 자체가 안 보인다.**
 * ★ 0건 조각은 띠에서는 안 보이지만 **범례에는 남는다** — 그것이 정보다.
 */
export function StackShare(p: ChartPanelProps) {
  const total = p.items.reduce((a, i) => a + i.count, 0);
  /** 램프에서 이 조각이 흰색에 섞인 정도(0=축색 그대로 · 1=거의 흰색). */
  const whiteness = (i: number) => (p.items.length <= 1 ? 0 : i / (p.items.length - 1)) * 0.72;
  const shade = (i: number) => {
    // 축색에서 옅어지는 램프. ★ 큰 조각일수록 진하다(원본 도넛과 같은 규칙).
    return `color-mix(in srgb, ${p.color} ${Math.round(100 - whiteness(i) * 100)}%, white)`;
  };
  /**
   * 조각 위 글자색.
   *
   * ⚠️⚠️ **늘 흰색이면 옅은 조각에서 안 읽힌다** — 램프 끝은 거의 흰색이라
   * 흰 글씨가 묻힌다(2026-10-04 실측: 라이트 모드 「20.0%」·「17.8%」).
   * ★ 다크에서도 같은 문제였는데 바탕이 어두워 덜 드러났을 뿐이다.
   * → **흰색에 40% 넘게 섞였으면 글자를 어둡게** 한다.
   *
   * ⚠️⚠️ **`var(--lens-text)` 를 쓰면 안 된다.** 그것은 **테마를 따르는** 색이라
   * 다크에서 밝다 — 옅은 조각 위에 밝은 글씨가 되어 **더 안 읽혔다**(실측).
   * ★ 조각 색은 테마와 무관하게 정해지므로 **글자도 고정값**이어야 한다.
   */
  const inkOn = (i: number) => (whiteness(i) > 0.4 ? '#131a2e' : '#ffffff');
  return (
    <Shell {...p}>
      <div className="flex h-10 w-full overflow-hidden rounded-md">
        {p.items.map((it, i) =>
          it.count === 0 ? null : (
            <span
              key={it.label}
              className="grid place-items-center text-label-sm"
              style={{
                width: `${(it.count / Math.max(total, 1)) * 100}%`,
                background: shade(i),
                color: inkOn(i),
              }}
              title={`${it.label} ${it.valueText} (${it.pctText})`}
            >
              {it.count / Math.max(total, 1) > 0.09 ? it.pctText : ''}
            </span>
          ),
        )}
      </div>
      <ul className="mt-lg grid gap-x-xl gap-y-sm [grid-template-columns:repeat(auto-fill,minmax(14rem,1fr))]">
        {p.items.map((it, i) => (
          <li key={it.label} className="flex items-center gap-sm text-body-md">
            <span
              className="h-3 w-3 shrink-0 rounded-sm"
              style={{ background: shade(i) }}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate">{it.label}</span>
            <span className="shrink-0 tabular-nums text-muted-foreground">{it.valueText}</span>
            <span className="w-14 shrink-0 text-right tabular-nums">{it.pctText}</span>
          </li>
        ))}
      </ul>
    </Shell>
  );
}

/**
 * 퍼널 — **계단형**. 첫 장면 대비로 줄어드는 모양을 본다.
 *
 * ⚠️ 각 줄을 100% 로 정규화하지 않는다 — 그러면 **줄어드는 것이 안 보인다.**
 */
export function FunnelSteps(p: ChartPanelProps) {
  const top = Math.max(...p.items.map((i) => i.count), 1);
  return (
    <Shell {...p}>
      <ul className="space-y-xs">
        {p.items.map((it, i) => {
          const drop = i > 0 ? p.items[i - 1]!.count - it.count : 0;
          return (
            <li key={it.label} className="flex items-center gap-md">
              <span className="w-28 shrink-0 truncate font-mono text-label-md">{it.label}</span>
              <span className="min-w-0 flex-1">
                <span
                  className="flex h-9 items-center rounded-sm px-md text-label-md text-white"
                  style={{
                    width: `${Math.max(6, (it.count / top) * 100)}%`,
                    background: it.tone === 'bad' ? 'var(--lens-error)' : p.color,
                  }}
                >
                  {it.valueText}
                </span>
              </span>
              <span className="w-20 shrink-0 text-right text-title-sm tabular-nums">
                {drop > 0 ? (
                  <span className={it.tone === 'bad' ? 'text-error' : 'text-muted-foreground'}>
                    -{drop}
                  </span>
                ) : (
                  <span className="text-muted-foreground">{i === 0 ? '기준' : ''}</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </Shell>
  );
}
